import dayjs from 'dayjs';
import {
  NexoyaContentRule,
  NexoyaContentV2,
  NexoyaEventCategory,
  NexoyaEventImpact,
  NexoyaPortfolioEvent,
  NexoyaPortfolioEventSnapshot,
} from '../../../types';
import { DuplicateEvent } from '../components/PortfolioEvents/PortfolioEventsFileUpload';
import { ENV_VARS_WRAPPER } from '../../../configs/envVariables';
import { ApolloClient, DocumentNode } from '@apollo/client';

export const GET_PORTFOLIO_EVENT_ASSET_API_URL = (portfolioEventId: string) =>
  window[ENV_VARS_WRAPPER]?.REACT_APP_API_BASE_URL.replace('graphql', '') +
  `portfolio-events/${portfolioEventId}/asset`;

export const createFormDataWithFile = (file: File | null | undefined, teamId: number) => {
  const formData = new FormData();
  if (file) {
    formData.append('file', file);
  }
  formData.append('teamId', teamId?.toString());
  return formData;
};

export const getPortfolioEventsWithinTimeframe = ({ portfolioEvents, start, end }) => {
  return portfolioEvents?.filter((event) => isPortfolioEventWithinTimeframe({ event, start, end }));
};

export const isPortfolioEventWithinTimeframe = ({ event, start, end }) => {
  const eventStart = dayjs(event.start);
  const eventEnd = dayjs(event.end);

  return (
    // Event starts within optimization period
    eventStart.isBetween(dayjs(start), dayjs(end), null, '[]') ||
    // Event ends within optimization period
    eventEnd.isBetween(dayjs(start), dayjs(end), null, '[]') ||
    // Event spans across optimization period
    (eventStart.isBefore(dayjs(start)) && eventEnd.isAfter(dayjs(end)))
  );
};

export const findDuplicateEvents = (
  newEvents: Partial<NexoyaPortfolioEvent>[],
  existingEvents: NexoyaPortfolioEvent[],
): DuplicateEvent[] => {
  return newEvents.reduce((duplicates: DuplicateEvent[], newEvent) => {
    const duplicate = existingEvents.find((existing) => existing.name === newEvent.name);
    if (duplicate) {
      duplicates.push({ existing: duplicate, new: newEvent });
    }
    return duplicates;
  }, []);
};

export const calculateImpactedContentsForEvent = ({
  contentRules,
  assignedContents,
}: {
  contentRules: NexoyaContentRule[];
  assignedContents: NexoyaContentV2[];
}) => {
  const contentRulesImpactedContents = contentRules?.reduce((acc, rule) => {
    return acc + rule.matchingDiscoveredContentsCount;
  }, 0);
  const assignedContentsCount = assignedContents?.length || 0;
  return contentRulesImpactedContents + assignedContentsCount;
};

export const calculateMaximumNumberOfOverlappingEvents = ({
  portfolioEvents,
}: {
  portfolioEvents: NexoyaPortfolioEvent[] | NexoyaPortfolioEventSnapshot[];
}): number => {
  if (!portfolioEvents || portfolioEvents.length === 0) {
    return 0;
  }

  // Create an array of time points with event start and end times
  const timePoints: { time: number; isStart: boolean }[] = [];

  // Process each event and add its start and end times to the timePoints array
  portfolioEvents.forEach((event) => {
    const startTime = new Date(event.start).getTime();
    const endTime = new Date(event.end).getTime();

    timePoints.push({ time: startTime, isStart: true });
    timePoints.push({ time: endTime, isStart: false });
  });

  // Sort by time, ends before starts on a tie — so events that end exactly when others start are
  // not counted as overlapping.
  timePoints.sort((a, b) => {
    if (a.time !== b.time) {
      return a.time - b.time;
    }
    // If times are equal, place end times first
    return a.isStart ? 1 : -1;
  });

  let currentOverlaps = 0;
  let maxOverlaps = 0;

  // Sweep through the time points
  timePoints.forEach((point) => {
    if (point.isStart) {
      // An event starts, increment the current overlap count
      currentOverlaps++;
      // Update the maximum overlap count if current is greater
      maxOverlaps = Math.max(maxOverlaps, currentOverlaps);
    } else {
      // An event ends, decrement the current overlap count
      currentOverlaps--;
    }
  });

  return maxOverlaps;
};

/**
 * Shared constants for event bar rendering — used by both addPortfolioEvents (amcharts)
 * and the chart container height calculations. Keep in sync.
 */
export const EVENT_BAR = {
  HEIGHT_COLLAPSED: 8,
  HEIGHT_EXTENDED: 24,
  SPACING: 6,
  MARGIN_TOP: 12,
} as const;

export const getEventBarHeight = (extended: boolean) =>
  extended ? EVENT_BAR.HEIGHT_EXTENDED : EVENT_BAR.HEIGHT_COLLAPSED;

export const calculateChartHeightWithEvents = ({
  baseHeight,
  hasEvents,
  maxOverlappingEvents,
  areEventsExtended,
}: {
  baseHeight: number;
  hasEvents: boolean;
  maxOverlappingEvents: number;
  areEventsExtended: boolean;
}): number => {
  if (!hasEvents || !maxOverlappingEvents) return baseHeight;
  const barHeight = getEventBarHeight(areEventsExtended);
  const eventsHeight = (barHeight + EVENT_BAR.SPACING + EVENT_BAR.MARGIN_TOP) * maxOverlappingEvents;
  return baseHeight + eventsHeight;
};

// Papa keeps a blank header cell (e.g. trailing comma) as the key "".
// Radix Select.Item throws on value="", so those columns are never offered.
export const getMappableCsvHeaders = (rows: Record<string, unknown>[] | undefined): string[] =>
  Object.keys(rows?.[0] ?? {}).filter((header) => header.trim() !== '');

export const convertToPortfolioEvents = ({ parsedData, columnMapping }): Partial<NexoyaPortfolioEvent>[] => {
  return parsedData.map((row: any) => ({
    __typename: 'PortfolioEvent',
    portfolioEventId: Math.random(),
    name: row[columnMapping.name],
    description: columnMapping.description ? row[columnMapping.description] : null,
    start: new Date(row[columnMapping.start]),
    end: new Date(row[columnMapping.end]),
    category: row[columnMapping.category] as NexoyaEventCategory,
    impact: row[columnMapping.impact] as NexoyaEventImpact,
    created: new Date().toISOString(),
  }));
};

export const fetchAllPortfolioEvents = async ({
  apolloClient,
  query,
  teamId,
  portfolioId,
  pageSize,
  maxPages,
}: {
  apolloClient: ApolloClient<object>;
  query: DocumentNode;
  teamId: number;
  portfolioId: number;
  pageSize: number;
  maxPages: number;
}): Promise<{ events: NexoyaPortfolioEvent[]; truncated: boolean; stalled: boolean }> => {
  const allEvents: NexoyaPortfolioEvent[] = [];
  let cursor: string | null = null;
  let hasNextPage = true;
  let pagesFetched = 0;

  while (hasNextPage && pagesFetched < maxPages) {
    pagesFetched++;

    const { data } = await apolloClient.query({
      query,
      variables: {
        teamId,
        portfolioId,
        first: pageSize,
        ...(cursor ? { after: cursor } : {}),
      },
      fetchPolicy: 'network-only',
    });

    const edges = data?.portfolioV2?.portfolioEvents?.edges || [];
    const events = edges.map((edge) => edge.node);

    if (events.length === 0) break;

    allEvents.push(...events);

    const pi = data?.portfolioV2?.portfolioEvents?.pageInfo;
    const newCursor = pi?.endCursor ?? null;

    if (newCursor === cursor) {
      return { events: allEvents, truncated: false, stalled: true };
    }

    hasNextPage = pi?.hasNextPage ?? false;
    cursor = newCursor;
  }

  return {
    events: allEvents,
    truncated: pagesFetched >= maxPages && hasNextPage,
    stalled: false,
  };
};

export const validateEvents = (events: NexoyaPortfolioEvent[]) => {
  const validImpacts = [NexoyaEventImpact.Large, NexoyaEventImpact.Small];
  const validCategories = [
    NexoyaEventCategory.BrandAwareness,
    NexoyaEventCategory.NegativeExternalEffects,
    NexoyaEventCategory.PositiveExternalEffects,
    NexoyaEventCategory.PriceIncrease,
    NexoyaEventCategory.PromotionAndDiscounts,
    NexoyaEventCategory.ProductLaunch,
    NexoyaEventCategory.TrackingIssue,
  ];

  const invalidEvents = [];

  events?.forEach((event, index) => {
    if (event.impact && !validImpacts.includes(event.impact)) {
      invalidEvents.push({
        index,
        field: 'impact',
        value: event.impact,
        allowed: validImpacts.join(', '),
      });
    }

    if (event.category && !validCategories.includes(event.category)) {
      invalidEvents.push({
        index,
        field: 'category',
        value: event.category,
        allowed: validCategories.join(', '),
      });
    }
  });

  return invalidEvents;
};
